import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, FileText } from "lucide-react";
import { PlatformShellHeader, PlatformShellFooter, useSeo } from "./PlatformShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/posts`).then((r) => {
      if (mounted) {
        setPosts(r.data || []);
        setLoading(false);
      }
    }).catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useSeo({
    title: "Insights & Guides — Boom Barrier Buying, FASTag, RFID & Gate Automation | Boom Barrier Company",
    description: "Expert guides on choosing automatic boom barriers in India — buyer guides, FASTag toll plaza integration, RFID vs ANPR, price comparisons and BBC product deep-dives.",
    canonical: "https://www.boombarriercompany.com/insights",
    image: "https://www.boombarriercompany.com/branded/bbc-pro.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Boom Barrier Company — Insights",
      "url": "https://www.boombarriercompany.com/insights",
      "blogPost": posts.map((p) => ({
        "@type": "BlogPosting",
        "headline": p.title,
        "description": p.meta_description,
        "url": `https://www.boombarriercompany.com/insights/${p.slug}`,
        "datePublished": p.published_at,
      })),
    },
  });

  return (
    <div data-testid="insights-root" className="min-h-screen bg-[#0F1113] text-white font-body antialiased selection:bg-[#00FF85]/40">
      <PlatformShellHeader />

      <section className="relative pt-20 md:pt-28 pb-12 border-b border-white/5">
        <div className="absolute inset-0 bbc-grid-bg opacity-[0.06] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3">
            <span className="w-8 h-px bg-[#00FF85]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85]">/// — Insights & Guides</span>
          </div>
          <h1 className="font-display mt-6 text-white text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.02em] leading-[0.98] max-w-4xl">
            Boom Barrier
            <br />
            <span className="text-[#00FF85]">Insights from the Field.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[#A1A1AA] text-base md:text-lg font-body leading-relaxed">
            Buyer's guides, FASTag toll plaza integration tutorials, RFID vs ANPR breakdowns, and honest price comparisons. Written for site managers, dealers and procurement teams across India.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div data-testid="insights-loading" className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#71717A]">Loading…</div>
          ) : posts.length === 0 ? (
            <div data-testid="insights-empty" className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#71717A]">No posts yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {posts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/insights/${p.slug}`}
                  data-testid={`insight-card-${p.slug}`}
                  className="group relative border border-white/10 bg-[#16191C] overflow-hidden hover:border-[#00FF85]/40 transition-all"
                >
                  {p.hero_image && (
                    <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#13161A] to-black">
                      <img src={p.hero_image} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                      <div className="absolute top-4 left-5">
                        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#00FF85] bg-black/50 backdrop-blur px-2 py-1 border border-[#00FF85]/30">
                          {p.category}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="p-6">
                    <h2 className="font-display text-white text-lg md:text-xl font-bold tracking-tight leading-tight line-clamp-3">
                      {p.title}
                    </h2>
                    <p className="mt-3 text-[#A1A1AA] text-sm font-body leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white group-hover:text-[#00FF85] transition">
                      Read insight <ArrowUpRight size={11} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA strip */}
      <section className="border-t border-white/5 py-16 bg-[#13161A]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <FileText className="mx-auto text-[#00FF85]" size={28} />
          <h2 className="font-display mt-4 text-white text-3xl md:text-4xl font-black tracking-[-0.02em]">
            Need a site-specific spec sheet?
          </h2>
          <p className="mt-3 text-[#A1A1AA] text-base font-body max-w-xl mx-auto">
            Share your site profile and our engineering team will spec the right barrier model + cabinet within one business day.
          </p>
          <Link
            to="/quote"
            data-testid="insights-cta-quote"
            className="mt-7 inline-flex items-center gap-3 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-6 py-3.5 text-xs uppercase tracking-wider transition shadow-[0_0_28px_rgba(0,255,133,0.4)]"
          >
            Request a Quote <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <PlatformShellFooter />
    </div>
  );
}
